// P4 — combined Patient + Clinician report generation, replacing the two
// separate 700-line components (Patient_Report.js, Clinician_Report.js).
// Confirmed real finding this merge is built on: /generate_report
// (backend main.py) already accepts report_kind as the ONLY axis that
// differs between the two flows — the PDF layout itself is driven by
// report_type_id (the lab/test-type axis), completely separate. So this
// is a genuine frontend-only merge: ONE Configuration card (lab + report
// type + file upload), firing /generate_report TWICE in parallel (one per
// report_kind) on submit, rendering both results together, and ONE shared
// identity form driving both GCS saves at once — staff fill out one form,
// get both PDFs, in one delivery step. Matches Proove's own dual-report
// (Opioid Risk/Pain Perception) generation pattern.
//
// Visual identity kept from both originals rather than picked one: the
// Patient section keeps its navy palette, the Clinician section keeps its
// green palette — same colors staff already associate with each flavor,
// just side-by-side instead of on two separate pages.
import React, { useState, useEffect, useRef } from "react";
import api from "../api";
import { extractErrorDetail } from "./validators";
import {
  Container, Typography, Button, CircularProgress,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Box, FormControl,
  InputLabel, Select, MenuItem, Alert, Chip, Fade, TextField,
} from "@mui/material";
import { UploadFile, Download, CloudUpload, CheckCircle, PictureAsPdf, CloudDone, LocalHospital } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "./PageShell";
import { EMPTY_ADDRESS, AddressFields, useAddressConfirm, isValidZip } from "./addressShared";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const NAVY = { navy: "#00357a", navyDark: "#001f4d", navyMid: "#004299", gold: "#c9a96e" };
const GREEN = { dark: "#044a37", mid: "#065f46", light: "#10b981", pale: "rgba(16,185,129,0.08)" };

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: "rgba(0,53,122,0.14)", borderRadius: "12px" },
    "&:hover fieldset": { borderColor: "#c9a96e" },
    "&.Mui-focused fieldset": { borderColor: "#00357a", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", fontFamily: "'Inter', sans-serif" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#00357a" },
};

const ANALYSIS_STEPS = [
  "Extracting metadata from pages…",
  "Analysing test panels…",
  "Generating patient + clinician summaries…",
  "Building lifestyle guidance & citations…",
  "Compiling both PDF reports…",
];

function OrbitalSpinner({ size = 56 }) {
  return (
    <Box sx={{ width: size, height: size, position: "relative", mx: "auto", mb: 3 }}>
      <Box sx={{
        position: "absolute", inset: 0, borderRadius: "50%",
        border: "2.5px solid transparent",
        borderTopColor: NAVY.navy, borderRightColor: `${NAVY.navy}40`,
        "@keyframes orb1": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
        animation: "orb1 1.1s linear infinite",
      }} />
      <Box sx={{
        position: "absolute", inset: 10, borderRadius: "50%",
        border: "2px solid transparent",
        borderBottomColor: NAVY.gold, borderLeftColor: `${NAVY.gold}40`,
        "@keyframes orb2": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(-360deg)" } },
        animation: "orb2 0.8s linear infinite",
      }} />
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        width: 7, height: 7, borderRadius: "50%", bgcolor: NAVY.navy,
        "@keyframes centerPulse": {
          "0%,100%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
          "50%": { opacity: 0.35, transform: "translate(-50%,-50%) scale(0.55)" },
        },
        animation: "centerPulse 1.5s ease-in-out infinite",
      }} />
    </Box>
  );
}

function AnalysisLoader({ active }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!active) { setStep(0); return; }
    const id = setInterval(() => setStep((s) => (s < ANALYSIS_STEPS.length - 1 ? s + 1 : s)), 4500);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;
  const pct = Math.round(((step + 1) / ANALYSIS_STEPS.length) * 100);

  return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <OrbitalSpinner />
      <AnimatePresence mode="wait">
        <motion.div key={step}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
          <Typography sx={{ fontSize: 15, color: "#475569", fontWeight: 500 }}>
            {ANALYSIS_STEPS[step]}
          </Typography>
        </motion.div>
      </AnimatePresence>
      <Box sx={{ width: 260, mx: "auto", mt: 3, mb: 1 }}>
        <Box sx={{ borderRadius: 99, overflow: "hidden", height: 4, bgcolor: "rgba(0,53,122,0.08)" }}>
          <motion.div
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ height: "100%", background: `linear-gradient(to right, ${NAVY.navyDark}, ${NAVY.navy}, ${GREEN.light})`, borderRadius: 99, position: "relative", overflow: "hidden" }}
          >
            <Box sx={{
              position: "absolute", inset: 0,
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
              backgroundSize: "200% 100%",
              "@keyframes pgShimmer": { "0%": { backgroundPosition: "-200% 0" }, "100%": { backgroundPosition: "200% 0" } },
              animation: "pgShimmer 1.8s linear infinite",
            }} />
          </motion.div>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.75, textAlign: "right" }}>{pct}%</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
        {ANALYSIS_STEPS.map((_, i) => (
          <motion.div key={i} animate={{ scale: i === step ? 1 : 0.85, opacity: i === step ? 1 : 0.4 }}
            transition={{ type: "spring", stiffness: 400, damping: 26 }}>
            <Box sx={{
              width: i === step ? 20 : 6, height: 6, borderRadius: 3,
              bgcolor: i === step ? NAVY.navy : "#cbd5e1",
              transition: "width 0.4s ease, background-color 0.3s ease",
            }} />
          </motion.div>
        ))}
      </Box>
    </Box>
  );
}

function DropZone({ files, onChange }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) => f.type === "application/pdf");
    if (dropped.length) onChange(dropped);
  };

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: `2px dashed ${dragging ? NAVY.navy : files.length ? "#10b981" : "#cbd5e1"}`,
        borderRadius: 3, p: 4, textAlign: "center", cursor: "pointer",
        bgcolor: dragging ? "rgba(0,53,122,0.04)" : files.length ? "rgba(16,185,129,0.03)" : "#fafbff",
        transition: "all 0.2s ease",
        "&:hover": { border: `2px dashed ${NAVY.navy}`, bgcolor: "rgba(0,53,122,0.04)" },
      }}>
      <input ref={inputRef} type="file" multiple accept="application/pdf" hidden
        onChange={(e) => onChange(Array.from(e.target.files))} />
      {files.length ? (
        <>
          <CheckCircle sx={{ fontSize: 36, color: "#10b981", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: "#059669", mb: 1 }}>
            {files.length} file{files.length > 1 ? "s" : ""} selected
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
            {files.map((f) => (
              <Chip key={f.name} label={f.name} size="small"
                sx={{ bgcolor: "rgba(16,185,129,0.1)", color: "#065f46", fontSize: 13 }} />
            ))}
          </Box>
        </>
      ) : (
        <>
          <CloudUpload sx={{ fontSize: 36, color: dragging ? NAVY.navy : "#94a3b8", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: dragging ? NAVY.navy : "#475569", mb: 0.5 }}>
            {dragging ? "Drop PDFs here" : "Drag & drop PDFs here"}
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: "#94a3b8" }}>or click to browse</Typography>
        </>
      )}
    </Box>
  );
}

function ResultSection({ title, children, delay = 0, theme = NAVY }) {
  const dark = theme === GREEN ? GREEN.dark : NAVY.navyDark;
  const mid = theme === GREEN ? GREEN.mid : NAVY.navy;
  return (
    <Fade in timeout={600} style={{ transitionDelay: `${delay}ms` }}>
      <Paper sx={{
        mb: 3, borderRadius: 3, overflow: "hidden",
        border: "1px solid rgba(0,53,122,0.1)",
        boxShadow: "0 4px 20px rgba(0,53,122,0.07), inset 0 1px 0 rgba(255,255,255,0.9)",
        backdropFilter: "blur(8px)", bgcolor: "rgba(255,255,255,0.93)",
      }}>
        <Box sx={{
          px: 3, py: 2, display: "flex", alignItems: "center", gap: 1,
          background: `linear-gradient(135deg, ${dark} 0%, ${mid} 100%)`,
        }}>
          <Box sx={{ width: 3, height: 20, borderRadius: 2, bgcolor: NAVY.gold }} />
          <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>{title}</Typography>
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Paper>
    </Fade>
  );
}

function getMetaValue(metadata, ...keys) {
  for (const key of keys) {
    const row = (metadata || []).find((r) => r.field?.toLowerCase().includes(key.toLowerCase()));
    if (row?.value && row.value !== "N/A") return row.value;
  }
  return "";
}

function normalizeDobToISO(dob) {
  const slashMatch = dob.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    const year = y.length === 2 ? (parseInt(y, 10) > 30 ? `19${y}` : `20${y}`) : y;
    return `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob;
  return "";
}

// ─── One report-result block, reused for both patient and clinician ───────
function ReportResultBlock({ report, theme, label }) {
  const dark = theme === GREEN ? GREEN.dark : NAVY.navyDark;
  const mid = theme === GREEN ? GREEN.mid : NAVY.navy;
  const accent = theme === GREEN ? GREEN.light : NAVY.gold;

  // 2026-10-21 fix (CONFIRMED real bug, found directly from the user's own
  // real screenshot: downloading/previewing a just-generated report showed
  // {"detail":"Could not validate credentials"} instead of the PDF) —
  // /api/download and /api/preview are both auth-gated (Depends(get_
  // current_user), a real Bearer JWT), but this used to hit them via a
  // bare window.open() and a plain <iframe src=...> — neither carries the
  // Authorization header `api`'s own axios instance attaches via
  // interceptor, so every one of these requests 401'd. Fetch the PDF
  // bytes through the authenticated `api` client instead (same pattern
  // LabOrdersView.js already uses for its own downloads, just via a blob
  // instead of a signed URL since these two routes return raw bytes, not
  // a redirect), then hand the browser a local blob: URL.
  const [previewBlobUrl, setPreviewBlobUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  useEffect(() => {
    let revoke = null;
    if (report?.preview_url) {
      api.get(report.preview_url, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoke = url;
          setPreviewBlobUrl(url);
        })
        .catch((err) => setPreviewError(extractErrorDetail(err, "Could not load the report preview.")));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [report?.preview_url]);

  const handleDownload = async () => {
    try {
      const res = await api.get(report.pdf_report, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      window.alert(extractErrorDetail(err, "Could not download this report right now."));
    }
  };

  if (!report) return null;
  return (
    <>
      <Fade in timeout={400}>
        <Paper sx={{
          mb: 3, borderRadius: 3, overflow: "hidden",
          border: "1px solid rgba(0,53,122,0.1)",
          boxShadow: "0 4px 24px rgba(0,53,122,0.08)",
          bgcolor: "rgba(255,255,255,0.93)",
        }}>
          <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${dark} 0%, ${mid} 100%)`, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <PictureAsPdf sx={{ color: accent, fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle sx={{ color: "#6ee7b7", fontSize: 15 }} />
                <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>{label} Report Generated</Typography>
              </Box>
            </Box>
            {report.pdf_report && (
              <Button variant="outlined" startIcon={<Download sx={{ fontSize: 15 }} />}
                onClick={handleDownload}
                sx={{ borderColor: "rgba(255,255,255,0.4)", color: accent, fontWeight: 600, fontSize: 14.5, textTransform: "none", borderRadius: "8px", px: 2.5, flexShrink: 0, whiteSpace: "nowrap", "&:hover": { borderColor: accent, bgcolor: "rgba(255,255,255,0.1)" } }}>
                Download PDF
              </Button>
            )}
          </Box>
          {report.preview_url && (
            <Box sx={{ p: 2.5, bgcolor: "rgba(0,53,122,0.03)", borderTop: "1px solid rgba(0,53,122,0.06)" }}>
              {previewError ? (
                <Typography sx={{ color: "#b91c1c", fontSize: 13.5 }}>{previewError}</Typography>
              ) : previewBlobUrl ? (
                <Box sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,31,77,0.14)", border: "1px solid rgba(0,53,122,0.1)" }}>
                  <iframe src={previewBlobUrl} title={`${label} Report Preview`} style={{ width: "100%", height: "600px", border: "none", display: "block" }} />
                </Box>
              ) : (
                <CircularProgress size={20} />
              )}
            </Box>
          )}
        </Paper>
      </Fade>

      <ResultSection title={`${label} Demographics`} theme={theme}>
        <TableContainer sx={{ borderRadius: "10px", border: "1px solid rgba(0,53,122,0.08)", overflow: "hidden" }}>
          <Table size="small"><TableBody>
            {(report.metadata || []).map((row, i) => (
              <TableRow key={row.field} sx={{ "&:last-child td": { border: 0 }, bgcolor: i % 2 === 0 ? "#fff" : "rgba(0,53,122,0.025)" }}>
                <TableCell sx={{ fontWeight: 700, color: mid, width: "32%", py: 1.6, fontSize: 14.5, borderRight: "1px solid rgba(0,53,122,0.06)" }}>{row.field}</TableCell>
                <TableCell sx={{ color: "#334155", fontSize: 14.5, py: 1.6 }}>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
        </TableContainer>
      </ResultSection>

      {report.tests?.length > 0 && (
        <ResultSection title={`${label} Test Results`} delay={100} theme={theme}>
          <TableContainer sx={{ borderRadius: "10px", border: "1px solid rgba(0,53,122,0.08)", overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(135deg, #eef2fb 0%, #e8edf8 100%)" }}>
                  {Object.keys(report.tests[0]).map((k) => (
                    <TableCell key={k} sx={{ fontWeight: 700, color: mid, fontSize: 13.5, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 }}>{k.replace(/_/g, " ")}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {report.tests.map((t, i) => (
                  <TableRow key={i} sx={{ "&:last-child td": { border: 0 }, bgcolor: i % 2 === 0 ? "#fff" : "rgba(0,53,122,0.022)" }}>
                    {Object.entries(t).map(([k, v]) => {
                      const ABNORMAL = ["High", "Low", "Abnormal", "Detected", "Positive", "Review", "Illicit"];
                      const isAbnormal = ABNORMAL.includes(v);
                      return (
                        <TableCell key={k} sx={{ fontSize: 14.5, py: 1.4, color: "#334155" }}>
                          {k === "status" ? (
                            <Chip label={String(v)} size="small" sx={{ bgcolor: isAbnormal ? "rgba(220,38,38,0.09)" : "rgba(22,163,74,0.09)", color: isAbnormal ? "#dc2626" : "#16a34a", fontWeight: 700, fontSize: 12.5, borderRadius: "6px", height: 22 }} />
                          ) : String(v)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ResultSection>
      )}

      {report.summary?.summary && (
        <ResultSection title={`${label} AI Summary`} delay={200} theme={theme}>
          <Box sx={{ "& h2,& h3": { color: mid, mt: 2, mb: 1 }, "& ul": { pl: 3 }, "& li": { mb: 0.5, color: "#334155", lineHeight: 1.8 }, "& p": { color: "#475569", lineHeight: 1.85, mb: 1.5 }, "& strong": { color: "#1e293b" } }}
            dangerouslySetInnerHTML={{ __html: report.summary.summary }} />
        </ResultSection>
      )}

      {report.lifestyle && (
        <ResultSection title={`${label} Lifestyle & Guidance`} delay={300} theme={theme}>
          <Box sx={{ "& h2,& h3": { color: mid, mt: 2, mb: 1 }, "& ul": { pl: 3 }, "& li": { mb: 0.5, color: "#334155", lineHeight: 1.8 }, "& p": { color: "#475569", lineHeight: 1.85, mb: 1.5 } }}
            dangerouslySetInnerHTML={{ __html: report.lifestyle }} />
        </ResultSection>
      )}
    </>
  );
}

export default function Report_Generation() {
  const [labs, setLabs] = useState([]);
  const [selectedLab, setSelectedLab] = useState("");
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedRT, setSelectedRT] = useState("");
  const [pdfFiles, setPdfFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Two separate report results — generated together, shown together.
  const [patientReport, setPatientReport] = useState(null);
  const [clinicianReport, setClinicianReport] = useState(null);

  // Shared identity form — drives BOTH GCS saves at once.
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientLastName, setPatientLastName] = useState("");
  const [patientDob, setPatientDob] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [providerNpi, setProviderNpi] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerPhone, setProviderPhone] = useState("");
  // Labarium identity plan, Part 3.1 — new identity-matching fields.
  const [accession, setAccession] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [patientAddress, setPatientAddress] = useState({ ...EMPTY_ADDRESS });
  // Labarium identity plan — second, independent address-confirmation
  // safety net (see addressShared.js's useAddressConfirm docstring).
  const { confirmAddress, dialog: addressConfirmDialog } = useAddressConfirm(API);

  const [uploadingGcs, setUploadingGcs] = useState(false);
  const [gcsUploaded, setGcsUploaded] = useState(false);
  const [gcsError, setGcsError] = useState("");
  // Tracked per-branch so a retry after a partial failure only re-sends
  // the branch that actually failed, instead of re-uploading (and
  // duplicating/re-notifying) the one that already succeeded.
  const [patientUploadDone, setPatientUploadDone] = useState(false);
  const [clinicianUploadDone, setClinicianUploadDone] = useState(false);

  useEffect(() => {
    // Real gap found and fixed: /api/config/labs returns EVERY lab
    // (infinitylabs AND medlake), not scoped to the logged-in user's own
    // lab (P2) — so the dropdown used to let staff pick a lab they
    // weren't assigned to, which then 403'd at submit time (the backend
    // enforces it correctly — this was a confusing UX dead end, not a
    // security gap). /api/auth/me already returns the real logged-in
    // user's own `lab` (UserOut, P2) — filter the dropdown down to just
    // that one lab, same real identity source the backend itself checks
    // against.
    Promise.all([
      api.get("/api/config/labs"),
      api.get("/api/auth/me"),
    ])
      .then(([labsRes, meRes]) => {
        const myLab = meRes.data?.lab;
        const scoped = myLab ? labsRes.data.filter((l) => l.lab_id === myLab) : labsRes.data;
        setLabs(scoped);
        if (scoped.length === 1) setSelectedLab(scoped[0].lab_id); // only one real option — pick it automatically, no need to make staff click it
      })
      .catch(() => setError("Failed to load lab configurations."));
  }, []);

  useEffect(() => {
    if (!selectedLab) { setReportTypes([]); setSelectedRT(""); return; }
    api.get(`/api/config/labs/${selectedLab}`)
      .then((r) => {
        const rts = Object.values(r.data.report_types || {});
        setReportTypes(rts);
        setSelectedRT(rts.length === 1 ? rts[0].id : "");
      })
      .catch(() => setError("Failed to load report types."));
  }, [selectedLab]);

  const canSubmit = pdfFiles.length > 0 && selectedLab && selectedRT && !loading;
  const generationIdRef = useRef(0);

  // ─── Generate BOTH flavors together, one click, same files ──────────────
  const handleGenerate = async () => {
    if (!pdfFiles.length) return setError("Please select PDF file(s).");
    if (!selectedLab) return setError("Please select a lab.");
    if (!selectedRT) return setError("Please select a report type.");
    const myGenerationId = ++generationIdRef.current;
    setLoading(true); setError("");
    setPatientReport(null); setClinicianReport(null);
    setGcsUploaded(false); setGcsError("");
    // Each new generate pass is for a (potentially) different patient —
    // never carry over the previous patient's identity fields, or a
    // staff member who forgets to clear them before saving could mail
    // Patient B's report to Patient A's address/email.
    setPatientFirstName(""); setPatientLastName(""); setPatientDob("");
    setPatientEmail(""); setPatientAddress({ ...EMPTY_ADDRESS });
    setAccession(""); setPracticeName(""); setProviderNpi(""); setProviderName(""); setProviderPhone("");
    setPatientUploadDone(false); setClinicianUploadDone(false);

    const buildFd = (kind) => {
      const fd = new FormData();
      fd.append("lab_id", selectedLab);
      fd.append("report_type_id", selectedRT);
      fd.append("report_kind", kind);
      pdfFiles.forEach((f) => fd.append("files", f));
      return fd;
    };

    try {
      // Fires both report_kind flavors in parallel — same backend
      // endpoint, same files, each flavor's own prompt template (see
      // this file's header comment for why that's safe to do in one step).
      const [patientRes, clinicianRes] = await Promise.all([
        api.post("/generate_report", buildFd("patient"), { headers: { "Content-Type": "multipart/form-data" } }),
        api.post("/generate_report", buildFd("clinician"), { headers: { "Content-Type": "multipart/form-data" } }),
      ]);
      // If the lab/report-type/files changed (or a new generate started)
      // while this request was in flight, this result is stale — discard
      // it instead of pairing new inputs with an old generate's output.
      if (generationIdRef.current !== myGenerationId) return;
      setPatientReport(patientRes.data);
      setClinicianReport(clinicianRes.data);

      // Prefill identity from whichever report's metadata has it (both
      // extract from the same source PDFs, so either should match).
      const meta = patientRes.data.metadata || clinicianRes.data.metadata || [];
      const rawName = getMetaValue(meta, "patient name", "patient", "name", "full name", "patient full name") || "";
      if (rawName.includes(",")) {
        const [last, first] = rawName.split(",").map((s) => s.trim());
        setPatientLastName(last || ""); setPatientFirstName(first || "");
      } else {
        const parts = rawName.trim().split(/\s+/);
        setPatientFirstName(parts[0] || ""); setPatientLastName(parts.slice(1).join(" ") || "");
      }
      const rawDob = getMetaValue(meta, "dob", "date of birth", "birth date", "dob/age", "date of birth (dob)") || "";
      if (rawDob) setPatientDob(normalizeDobToISO(rawDob));
      // Labarium identity plan, Part 4 — same OCR-prefill pattern as
      // name/DOB above. Staff review/correct before saving; never
      // silently trusted as-is (the backend's own random-hex fallback was
      // removed for the same reason).
      setAccession(patientRes.data.accession || clinicianRes.data.accession || "");
    } catch (e) {
      if (generationIdRef.current !== myGenerationId) return;
      setError(extractErrorDetail(e, "We couldn't process this report. Please try again or contact support."));
    }
    if (generationIdRef.current === myGenerationId) setLoading(false);
  };

  // ─── ONE delivery step: saves BOTH PDFs to cloud storage together ───────
  const handleUploadBothToGcs = async () => {
    if (!patientReport?.pdf_filename && !clinicianReport?.pdf_filename) return;
    if (!patientFirstName.trim()) return setGcsError("Please enter the patient first name.");
    if (!patientLastName.trim()) return setGcsError("Please enter the patient last name.");
    if (!patientDob.trim()) return setGcsError("Please select the patient date of birth.");
    if (!practiceName.trim()) return setGcsError("Please enter the ordering practice name.");
    if (!providerNpi.trim()) return setGcsError("Please enter the ordering provider NPI.");
    // 2026-10-15 fix (proper mandatory validation) — NPI had no format
    // check anywhere in this app, frontend or backend, despite the exact
    // 10-digit rule already existing elsewhere in the backend (RCM filter).
    if (!/^\d{10}$/.test(providerNpi.trim())) return setGcsError("Provider NPI must be exactly 10 digits.");
    if (!providerName.trim()) return setGcsError("Please enter the ordering provider name.");
    // 2026-10-23 (clinician invite-only build, Piece 7) — same mandatory
    // treatment as practiceName/providerNpi/providerName directly above.
    if (!providerPhone.trim()) return setGcsError("Please enter the ordering provider's phone number.");
    if (providerPhone.replace(/\D/g, "").length < 10) return setGcsError("Provider phone must be a real 10-digit phone number.");
    // Labarium identity plan, Part 3/4 — same required treatment as
    // name/DOB above; the backend enforces these too (defense in depth,
    // not trusting client-side validation alone).
    if (!accession.trim()) return setGcsError("Please enter the accession number.");
    // 2026-10-22 (Piece 4, one-identity-one-email invariant) — patient_email
    // is now unconditionally required by the backend, regardless of
    // report_kind: resolve_identity runs underneath EITHER flavor, and its
    // email-claim check needs an email to check against no matter which
    // copy is being archived. Previously this was only required for the
    // patient-kind upload (the report-ready notification target) — a
    // clinician-only copy could silently skip identity-claim checking
    // entirely, the exact gap the Proove Upload reversal (Piece 5) closed
    // for the same reason.
    if (!patientEmail.trim()) return setGcsError("Please enter the patient's email address.");
    if (!patientAddress.line1.trim() || !patientAddress.city.trim() || !patientAddress.state.trim() || !patientAddress.zip.trim()) {
      return setGcsError("Please enter the patient's address.");
    }
    // 2026-10-15 fix (proper mandatory validation) — mirrors AddressFields'
    // own inline state/zip format validation at the gate that actually
    // blocks submission, not just visually.
    if (!isValidZip(patientAddress.zip)) return setGcsError("Please enter a valid 5-digit ZIP code.");

    // Labarium identity plan — forced confirm checkpoint, right before this
    // upload, using the backend's authoritative Smarty validation.
    const confirmedAddress = await confirmAddress(patientAddress);
    if (!confirmedAddress) return; // "No, let me edit" — don't upload

    setUploadingGcs(true); setGcsError("");
    try {
      const uploads = [];
      const branches = [];
      if (patientReport?.pdf_filename && !patientUploadDone) {
        const fd = new FormData();
        fd.append("pdf_filename", patientReport.pdf_filename);
        fd.append("report_kind", "patient");
        fd.append("lab_id", selectedLab);
        fd.append("report_type_id", selectedRT);
        fd.append("ai_summary", patientReport.summary?.summary || ""); // P5 — already in state from /generate_report's own response
        // Part 10 — same round-trip pattern as ai_summary above, patient-kind
        // upload ONLY (never sent on the clinician branch below — see that
        // branch's own comment and the backend's matching report_kind=="patient"
        // scoping check in upload_report_to_gcs).
        if (patientReport.structured_results) {
          fd.append("structured_results", JSON.stringify(patientReport.structured_results));
        }
        // Phase 0 ("My Results" foundation) — same round-trip pattern as
        // structured_results above: both already sit in this report's own
        // /generate_report response state (summary/metadata/lifestyle),
        // just never persisted anywhere before now.
        if (patientReport.metadata) {
          fd.append("report_metadata", JSON.stringify(patientReport.metadata));
        }
        if (patientReport.lifestyle) {
          fd.append("lifestyle_html", patientReport.lifestyle);
        }
        fd.append("patient_first_name", patientFirstName.trim());
        fd.append("patient_last_name", patientLastName.trim());
        fd.append("patient_dob", patientDob.trim());
        fd.append("accession", accession.trim());
        fd.append("patient_email", patientEmail.trim());
        fd.append("patient_address_line1", confirmedAddress.line1.trim());
        fd.append("patient_address_unit", (confirmedAddress.unit || "").trim());
        fd.append("patient_address_city", confirmedAddress.city.trim());
        fd.append("patient_address_state", confirmedAddress.state.trim());
        fd.append("patient_address_zip", confirmedAddress.zip.trim());
        // 2026-10-24 fix (flow audit, user-directed) — practice_name/
        // provider_name were previously only ever sent on the clinician
        // branch below, even though they're the SAME typed-once values
        // validated unconditionally at the top of this function.
        // _write_report_summary_doc already accepts them on either
        // report_kind — the patient-flavor row just never got them, so
        // the patient's own My Summaries/My Results page could never
        // show "Ordered by" for a native report. provider_npi/
        // provider_phone are deliberately NOT included here — the user
        // explicitly confirmed only practice name + provider name should
        // ever be patient-visible, and neither the "Ordered by" chip nor
        // any other patient-facing field reads NPI, so there's no reason
        // to put it on this row at all (unlike practice_name/
        // provider_name, which the chip does read).
        fd.append("practice_name", practiceName.trim());
        fd.append("provider_name", providerName.trim());
        uploads.push(api.post("/api/reports/upload", fd));
        branches.push("patient");
      }
      if (clinicianReport?.pdf_filename && !clinicianUploadDone) {
        const fd = new FormData();
        fd.append("pdf_filename", clinicianReport.pdf_filename);
        fd.append("report_kind", "clinician");
        fd.append("lab_id", selectedLab);
        fd.append("report_type_id", selectedRT);
        fd.append("ai_summary", clinicianReport.summary?.summary || ""); // P5
        fd.append("practice_name", practiceName.trim());
        fd.append("provider_npi", providerNpi.trim());
        fd.append("provider_name", providerName.trim());
        fd.append("provider_phone", providerPhone.trim());
        // Phase 0 ("My Results" foundation) — same round-trip as the
        // patient branch above, this flavor's own separately-generated
        // metadata/lifestyle content (never conflated with the patient
        // branch's own copy).
        if (clinicianReport.metadata) {
          fd.append("report_metadata", JSON.stringify(clinicianReport.metadata));
        }
        if (clinicianReport.lifestyle) {
          fd.append("lifestyle_html", clinicianReport.lifestyle);
        }
        fd.append("patient_first_name", patientFirstName.trim());
        fd.append("patient_last_name", patientLastName.trim());
        fd.append("patient_dob", patientDob.trim());
        fd.append("accession", accession.trim());
        // 2026-10-22 (Piece 4) — now sent on the clinician branch too; see
        // the comment on this function's own patient_email validation
        // check above for why. Both branches resolve the same identity
        // and must both carry the email the identity-claim check runs
        // against.
        fd.append("patient_email", patientEmail.trim());
        fd.append("patient_address_line1", confirmedAddress.line1.trim());
        fd.append("patient_address_unit", (confirmedAddress.unit || "").trim());
        fd.append("patient_address_city", confirmedAddress.city.trim());
        fd.append("patient_address_state", confirmedAddress.state.trim());
        fd.append("patient_address_zip", confirmedAddress.zip.trim());
        uploads.push(api.post("/api/reports/upload", fd));
        branches.push("clinician");
      }
      const results = await Promise.allSettled(uploads);
      const failedBranches = [];
      let firstFailureDetail = "";
      results.forEach((r, i) => {
        const branch = branches[i];
        if (r.status === "fulfilled") {
          if (branch === "patient") setPatientUploadDone(true);
          if (branch === "clinician") setClinicianUploadDone(true);
        } else {
          failedBranches.push(branch);
          // 2026-10-22 (Piece 4) — both flavors resolve the same identity
          // against the same patient_email, so an identity-email-conflict
          // 409 is the real backend response, not a generic save failure —
          // the admin-recovery guidance in its `detail` (with the masked
          // on-file email) needs to actually reach this screen, not be
          // swallowed by the generic message below.
          if (!firstFailureDetail) firstFailureDetail = extractErrorDetail(r.reason, "");
        }
      });
      if (failedBranches.length) {
        setGcsError(
          firstFailureDetail
            ? `Failed to save: ${failedBranches.join(", ")}. ${firstFailureDetail}`
            : `Failed to save: ${failedBranches.join(", ")}. The other copy was saved — click "Save" again to retry only what failed.`
        );
      } else {
        setGcsUploaded(true);
      }
    } catch (e) {
      setGcsError(extractErrorDetail(e, "We couldn't upload this file. Please try again."));
    }
    setUploadingGcs(false);
  };

  const hasResults = patientReport || clinicianReport;

  return (
    <PageShell>
      {addressConfirmDialog}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: NAVY.navy, letterSpacing: "-0.02em", fontFamily: "'Playfair Display', serif" }}>
              Generate Report
            </Typography>
            <Typography sx={{ color: "#64748b", mt: 0.75, fontFamily: "'Inter', sans-serif", fontSize: 15 }}>
              Upload a lab PDF once — generates both the patient-friendly report and the clinician decision-support
              report together, delivered in one step.
            </Typography>
          </Box>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Configuration — ONE shared card for both flavors ── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
          <Paper sx={{ mb: 3, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,53,122,0.1)", boxShadow: "0 4px 24px rgba(0,53,122,0.08)" }}>
            <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${NAVY.navyDark} 0%, ${NAVY.navy} 100%)`, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 3, height: 20, borderRadius: 2, bgcolor: NAVY.gold }} />
              <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>Configuration</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <FormControl size="small" sx={selectSx} disabled={loading}>
                  <InputLabel>Laboratory</InputLabel>
                  <Select value={selectedLab} label="Laboratory" onChange={(e) => { setSelectedLab(e.target.value); setPatientReport(null); setClinicianReport(null); }}>
                    {labs.map((l) => <MenuItem key={l.lab_id} value={l.lab_id}>{l.display_name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={selectSx} disabled={!selectedLab || loading}>
                  <InputLabel>Report Type</InputLabel>
                  <Select value={selectedRT} label="Report Type" onChange={(e) => { setSelectedRT(e.target.value); setPatientReport(null); setClinicianReport(null); }}>
                    {reportTypes.map((rt) => (
                      <MenuItem key={rt.id} value={rt.id}>
                        {rt.name}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({rt.extraction_type})</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <DropZone files={pdfFiles} onChange={(f) => { if (loading) return; setPdfFiles(f); setPatientReport(null); setClinicianReport(null); }} />
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" onClick={handleGenerate} disabled={!canSubmit}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
                  sx={{ bgcolor: NAVY.navy, "&:hover": { bgcolor: NAVY.navyDark }, borderRadius: 2, px: 4, py: 1.2, fontWeight: 700, textTransform: "none", fontSize: 14 }}>
                  {loading ? "Analysing…" : "Generate Both Reports"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        <AnimatePresence>
          {loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Paper sx={{ mb: 3, borderRadius: 3, border: "1px solid rgba(0,53,122,0.08)" }}>
                <AnalysisLoader active={loading} />
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── ONE shared identity form + delivery step for BOTH reports ── */}
        {hasResults && !loading && (
          <Fade in timeout={300}>
            <Paper sx={{ mb: 3, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,53,122,0.1)", boxShadow: "0 4px 24px rgba(0,53,122,0.08)" }}>
              <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${NAVY.navyDark} 0%, ${NAVY.navy} 100%)` }}>
                <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>Save Both Reports to Cloud Storage</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {gcsUploaded ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CloudDone sx={{ color: "#10b981", fontSize: 24 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "#059669", fontSize: 14 }}>Both reports saved to Cloud Storage</Typography>
                      <Typography sx={{ fontSize: 13.5, color: "#94a3b8" }}>
                        {[patientFirstName, patientLastName].filter(Boolean).join(" ") || "—"} / {patientDob || "—"}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                      <TextField label="Patient First Name" size="small" value={patientFirstName} onChange={(e) => setPatientFirstName(e.target.value)} sx={{ flex: 1, minWidth: 150, ...selectSx }} />
                      <TextField label="Patient Last Name" size="small" value={patientLastName} onChange={(e) => setPatientLastName(e.target.value)} sx={{ flex: 1, minWidth: 150, ...selectSx }} />
                      <TextField label="Date of Birth" type="date" size="small" value={patientDob} onChange={(e) => setPatientDob(e.target.value)} sx={{ width: 180, ...selectSx }} InputLabelProps={{ shrink: true }} />
                      <TextField label="Accession #" size="small" value={accession} onChange={(e) => setAccession(e.target.value)} sx={{ width: 180, ...selectSx }} />
                    </Box>
                    {/* Labarium identity plan, Part 3 — email + address.
                        Pre-fill is OCR/name-field-driven elsewhere in this
                        form; these two are staff-entered (no OCR source —
                        source lab PDFs never carry a patient address). */}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                      {/* 2026-10-22 (Piece 4, ui-ux-checker finding) — now
                          required even for a clinician-only save, which
                          wasn't true before; without an explanation this
                          reads as an arbitrary new blocker to staff doing
                          a clinician-copy-only upload. */}
                      <TextField label="Patient Email" type="email" size="small" value={patientEmail} onChange={(e) => setPatientEmail(e.target.value)}
                        helperText="Required to verify this patient's identity on file, even for a clinician-only report."
                        sx={{ flex: 1, minWidth: 220, ...selectSx }} />
                    </Box>
                    <Box sx={{ mb: 2, maxWidth: 480 }}>
                      <AddressFields isDark={false} inputSx={selectSx} value={patientAddress} onChange={setPatientAddress} showErrors />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <LocalHospital sx={{ color: GREEN.mid, fontSize: 16 }} />
                      <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: GREEN.mid }}>Ordering Provider — for the clinician copy</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <TextField label="Practice Name" size="small" value={practiceName} onChange={(e) => setPracticeName(e.target.value)} sx={{ flex: 1, minWidth: 180, ...selectSx }} />
                      <TextField label="Provider Name" size="small" value={providerName} onChange={(e) => setProviderName(e.target.value)} sx={{ flex: 1, minWidth: 180, ...selectSx }} />
                      <TextField label="Provider NPI" size="small" value={providerNpi}
                        onChange={(e) => setProviderNpi(e.target.value.replace(/\D/g, ""))}
                        inputProps={{ maxLength: 10, inputMode: "numeric" }}
                        error={!!providerNpi && providerNpi.length !== 10} helperText={!!providerNpi && providerNpi.length !== 10 ? "10 digits" : ""}
                        sx={{ width: 180, ...selectSx }} />
                      <TextField label="Provider Phone" size="small" value={providerPhone}
                        onChange={(e) => setProviderPhone(e.target.value)}
                        error={!!providerPhone && providerPhone.replace(/\D/g, "").length < 10}
                        helperText={!!providerPhone && providerPhone.replace(/\D/g, "").length < 10 ? "10 digits" : ""}
                        sx={{ width: 180, ...selectSx }} />
                      <Button variant="contained" onClick={handleUploadBothToGcs} disabled={uploadingGcs}
                        startIcon={uploadingGcs ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <CloudUpload sx={{ fontSize: 15 }} />}
                        sx={{ bgcolor: NAVY.navy, "&:hover": { bgcolor: NAVY.navyDark }, fontWeight: 700, fontSize: 14.5, textTransform: "none", borderRadius: "8px", px: 3, py: "8px" }}>
                        {uploadingGcs ? "Uploading…" : "Save Both to Cloud"}
                      </Button>
                    </Box>
                    {gcsError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }} onClose={() => setGcsError("")}>{gcsError}</Alert>}
                  </>
                )}
              </Box>
            </Paper>
          </Fade>
        )}

        {/* ── Both report results, shown together ── */}
        {!loading && (
          <>
            <ReportResultBlock report={patientReport} theme={NAVY} label="Patient" />
            <ReportResultBlock report={clinicianReport} theme={GREEN} label="Clinician" />
          </>
        )}

        {hasResults && !loading && (
          <Fade in timeout={600}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: "rgba(192,168,140,0.08)", border: "1px solid rgba(192,168,140,0.25)" }}>
              <Typography sx={{ fontSize: 14, color: "#78716c", lineHeight: 1.7 }}>
                <strong>Disclaimer:</strong> The above content was generated with the help of Artificial Intelligence
                and is for informational purposes only. It is not a diagnosis, medical advice, or treatment. Please
                consult a licensed healthcare provider.
              </Typography>
            </Box>
          </Fade>
        )}
      </Container>
    </PageShell>
  );
}
